import { ElementOf } from "@shared/common/types/typeHelpers";
import { DateTime, Duration } from "luxon";
import { createSchema } from "../zod-utils";
import { z } from "zod/v3";
import { PlainMessage, Timestamp, Duration as ProtoDuration } from "@bufbuild/protobuf";
import {
  HipTask as ProtoHipTask,
  HipTask_TaskStatus,
  HipTask_FailureReason,
} from "@opstel/gen/knop/ui/v1/api/hip_pb";

const HIP_TASK_STATUS = [
  "Unknown",
  "InQueue",
  "Running",
  "Completed",
  "FailedWithResult",
  "Failed",
  "SentToDonna",
] as const;

export type HipTaskStatus = ElementOf<typeof HIP_TASK_STATUS>;

const HIP_FAILURE_REASON = ["NoMatching"] as const;

export type HipTaskFailureReason = ElementOf<typeof HIP_FAILURE_REASON>;

export interface HipTask {
  taskId: string;
  scenarioStart: DateTime;
  scenarioEnd: DateTime;
  numberOfConflicts?: number;
  status: HipTaskStatus;
  scenarioHub: string;
  requestedAt: DateTime;
  maximumComputationTime?: Duration;
  taskStartedAt?: DateTime;
  queuePosition?: number;
  correlationHipId: string;
  correlationOperationId: string;
  correlationParentId: string;
  failureReason?: HipTaskFailureReason;
}

function transformHipTaskStatusEnum(interaction: HipTask_TaskStatus): HipTaskStatus {
  return HIP_TASK_STATUS[interaction];
}

function transformHipTaskFailureReasonEnum(
  failureReason: HipTask_FailureReason,
): HipTaskFailureReason | undefined {
  const mapping: Record<HipTask_FailureReason, HipTaskFailureReason | undefined> = {
    [HipTask_FailureReason.NO_MATCHING]: "NoMatching",
    [HipTask_FailureReason.FAILURE_REASON_UNKNOWN]: undefined,
    [HipTask_FailureReason.UNSPECIFIED]: undefined,
  };
  return mapping[failureReason];
}

export const hipTaskSchema = createSchema<HipTask, PlainMessage<ProtoHipTask>>({
  taskId: z.string().min(1, { message: "HipTask taskId should not be empty" }),
  scenarioStart: z
    .instanceof(Timestamp, { message: "HipTask scenarioStart should not be empty" })
    .refine((timestamp) => timestamp)
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  scenarioEnd: z
    .instanceof(Timestamp, { message: "HipTask scenarioEnd should not be empty" })
    .transform((val) => DateTime.fromJSDate(val.toDate())),
  numberOfConflicts: z.number(),
  status: z
    .nativeEnum(HipTask_TaskStatus)
    .transform(transformHipTaskStatusEnum)
    .refine((status) => status !== "Unknown", { message: "HipTask status should not be unknown" }),
  scenarioHub: z.string(),
  requestedAt: z.instanceof(Timestamp).transform((val) => DateTime.fromJSDate(val.toDate())),
  maximumComputationTime: z
    .instanceof(ProtoDuration)
    .transform((val) => Duration.fromObject({ seconds: Number(val.seconds) }))
    .refine((val) => val.as("seconds") >= 0, {
      message: "HipTask maximumComputationTime should be positive",
    })
    .optional(),
  taskStartedAt: z
    .instanceof(Timestamp)
    .transform((val) => DateTime.fromJSDate(val.toDate()))
    .optional(),
  queuePosition: z.number().optional(),
  correlationHipId: z.string(),
  correlationOperationId: z.string(),
  correlationParentId: z.string(),
  failureReason: z
    .nativeEnum(HipTask_FailureReason)
    .transform(transformHipTaskFailureReasonEnum)
    .optional(),
});
